/**
 * m3 echo Frontend - Homepage URL Dialog Component (V3.0)
 *
 * Dialog to configure/update the homepage URL for a project.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { useUpdateProjectHomepage } from '../hooks/useOptimization';

interface HomepageUrlDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: number;
  currentUrl?: string | null;
  onSuccess?: (newUrl: string) => void;
}

const HomepageUrlDialog: React.FC<HomepageUrlDialogProps> = ({
  open,
  onClose,
  projectId,
  currentUrl,
  onSuccess,
}) => {
  const [url, setUrl] = useState(currentUrl || '');
  const [error, setError] = useState<string | null>(null);

  const updateMutation = useUpdateProjectHomepage();

  // Update local state when currentUrl changes
  useEffect(() => {
    setUrl(currentUrl || '');
  }, [currentUrl]);

  /**
   * Normalizes a URL by prepending https:// if no protocol is given
   */
  const normalizeUrl = (value: string): string => {
    const trimmed = value.trim();
    if (trimmed && !/^https?:\/\//i.test(trimmed)) {
      return `https://${trimmed}`;
    }
    return trimmed;
  };

  /**
   * Validates URL format
   */
  const validateUrl = (value: string): boolean => {
    if (!value.trim()) {
      setError('URL darf nicht leer sein');
      return false;
    }

    try {
      const urlObj = new URL(normalizeUrl(value));
      if (!urlObj.protocol.startsWith('http')) {
        setError('URL muss mit http:// oder https:// beginnen');
        return false;
      }
      setError(null);
      return true;
    } catch (e) {
      setError('Ungültige URL. Beispiel: www.example.com');
      return false;
    }
  };

  /**
   * Handles URL input change
   */
  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setUrl(value);
    if (value.trim()) {
      validateUrl(value);
    } else {
      setError(null);
    }
  };

  /**
   * Handles form submission
   */
  const handleSubmit = async () => {
    if (!validateUrl(url)) {
      return;
    }

    const normalizedUrl = normalizeUrl(url);

    try {
      await updateMutation.mutateAsync({
        projectId,
        data: { homepage_url: normalizedUrl },
      });

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess(normalizedUrl);
      }

      // Close dialog on success
      onClose();
    } catch (err) {
      setError('Fehler beim Speichern der URL. Bitte versuchen Sie es erneut.');
    }
  };

  /**
   * Handles dialog close
   */
  const handleClose = () => {
    setError(null);
    setUrl(currentUrl || '');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {currentUrl ? 'Homepage-URL ändern' : 'Homepage-URL konfigurieren'}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Geben Sie die URL Ihrer Unternehmens-Homepage ein. Diese wird verwendet, um Ihre
            Web-Präsenz zu analysieren und Optimierungsempfehlungen zu generieren.
          </Typography>

          <TextField
            autoFocus
            margin="normal"
            label="Homepage URL"
            type="url"
            fullWidth
            value={url}
            onChange={handleUrlChange}
            error={!!error}
            helperText={error || 'Beispiel: www.example.com oder https://www.example.com'}
            placeholder="www.example.com"
          />

          {updateMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Fehler beim Speichern der URL. Bitte versuchen Sie es erneut.
            </Alert>
          )}

          {currentUrl && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Aktuelle URL: {currentUrl}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={updateMutation.isPending}>
          Abbrechen
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={updateMutation.isPending || !!error || !url.trim()}
        >
          {updateMutation.isPending ? 'Speichere...' : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HomepageUrlDialog;
